import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useTheme } from '@/theme';
import { useTranslation } from 'react-i18next';
import {
  type FullTimeline,
  type TimelineCategory,
  CATEGORY_I18N_KEY,
  getCategoryColor,
  formatDuration,
} from './timelineUtils';

interface TimelineChartProps {
  timeline: FullTimeline;
  showCharging: boolean;
  chartRef?: React.RefObject<any>;
}

const CATEGORIES_ALL: TimelineCategory[] = ['operating', 'ble', 'charging'];
const CHART_HEIGHT_BASE = 180;
const ROW_HEIGHT = 50;

export const TimelineChart: React.FC<TimelineChartProps> = ({
  timeline,
  showCharging,
  chartRef,
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const categories = showCharging ? CATEGORIES_ALL : CATEGORIES_ALL.filter(c => c !== 'charging');
  const chartHeight = CHART_HEIGHT_BASE + categories.length * ROW_HEIGHT;

  const option = useMemo((): EChartsOption => {
    const { intervals, pointEvents, rangeStart, rangeEnd } = timeline;

    const renderItem = (_params: any, api: any) => {
      const categoryIndex = api.value(0);
      const start = api.coord([api.value(1), categoryIndex]);
      const end = api.coord([api.value(2), categoryIndex]);
      const height = ROW_HEIGHT * 0.55;

      return {
        type: 'rect',
        transition: ['shape'],
        shape: {
          x: start[0],
          y: start[1] - height / 2,
          width: Math.max(end[0] - start[0], 2),
          height,
          r: 3,
        },
        style: {
          fill: api.value(3) as string,
          opacity: 0.85,
        },
        emphasis: {
          style: { opacity: 1 },
        },
      };
    };

    // [categoryIndex, startMs, endMs, color, durationLabel]
    const data: any[] = intervals
      .filter(i => categories.includes(i.category))
      .map(i => {
        const catIdx = categories.indexOf(i.category);
        return [
          catIdx,
          i.start.getTime(),
          i.end.getTime(),
          getCategoryColor(i.category, theme),
          formatDuration(i.durationMs),
        ];
      });

    const series: any[] = [
      {
        type: 'custom',
        renderItem,
        encode: { x: [1, 2], y: 0 },
        data,
        clip: true,
      },
    ];

    const scatterData = pointEvents.map(pe => {
      const color =
        pe.severity === 'critical'
          ? (theme.colors.status.danger.fill.surface as string)
          : pe.severity === 'error'
            ? (theme.colors.status.caution.fill.surface as string)
            : theme.colors.text.secondary;
      return {
        value: [pe.time.getTime(), 0],
        itemStyle: { color },
        name: pe.label,
        eventData: pe.eventData,
        description: pe.description,
      };
    });

    if (scatterData.length > 0) {
      series.push({
        type: 'scatter',
        symbol: 'diamond',
        symbolSize: 10,
        data: scatterData,
        z: 10,
        tooltip: {
          formatter: (params: any) => {
            const d = new Date(params.value[0]);
            const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            const item = params.data;
            let html = `<b>${params.name}</b><br/>${date} ${time}`;
            if (item.eventData && item.eventData !== '-') html += `<br/>Data: ${item.eventData}`;
            if (item.description && item.description !== '-') html += `<br/>${item.description}`;
            return html;
          },
        },
      });
    }

    const categoryLabels = categories.map(c => t(CATEGORY_I18N_KEY[c]));

    const operatingColor = getCategoryColor('operating', theme);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (params: any) => {
          if (params.componentSubType === 'scatter') return '';
          const val = params.value;
          if (!val || val.length < 5) return '';
          const cat = categories[val[0]];
          const start = new Date(val[1]);
          const end = new Date(val[2]);
          const fmtDate = (d: Date) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const fmtTime = (d: Date) =>
            `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
          const sameDay = fmtDate(start) === fmtDate(end);
          const rangeStr = sameDay
            ? `${fmtDate(start)} ${fmtTime(start)} – ${fmtTime(end)}`
            : `${fmtDate(start)} ${fmtTime(start)} – ${fmtDate(end)} ${fmtTime(end)}`;
          return `<b>${t(CATEGORY_I18N_KEY[cat])}</b><br/>${rangeStr}<br/>${val[4]}`;
        },
      },
      grid: {
        left: 120,
        right: 40,
        top: 15,
        bottom: 70,
      },
      xAxis: {
        type: 'time',
        min: rangeStart.getTime(),
        max: rangeEnd.getTime(),
        axisLabel: {
          color: theme.colors.text.secondary,
          fontSize: 11,
          formatter: (value: number) => {
            const d = new Date(value);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            return `${mm}-${dd}\n${hh}:00`;
          },
        },
        splitLine: {
          show: true,
          lineStyle: { color: theme.colors.border.divider, type: 'dashed' },
        },
        axisLine: { lineStyle: { color: theme.colors.border.container } },
        axisTick: { lineStyle: { color: theme.colors.border.container } },
      },
      yAxis: {
        type: 'category',
        data: categoryLabels,
        inverse: true,
        axisLabel: {
          color: theme.colors.text.primary,
          fontSize: 12,
          fontWeight: 500,
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'weakFilter',
          height: 24,
          bottom: 10,
          borderColor: theme.colors.border.container,
          backgroundColor: theme.colors.surface.layout.background,
          dataBackground: {
            lineStyle: { color: theme.colors.border.divider },
            areaStyle: { color: theme.colors.border.divider + '40' },
          },
          selectedDataBackground: {
            lineStyle: { color: operatingColor },
            areaStyle: { color: operatingColor + '30' },
          },
          handleStyle: { color: theme.colors.button.bg.primary },
          textStyle: { color: theme.colors.text.secondary, fontSize: 10 },
          labelFormatter: (value: number) => {
            const d = new Date(value);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const mi = String(d.getMinutes()).padStart(2, '0');
            return `${mm}-${dd} ${hh}:${mi}`;
          },
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'weakFilter',
        },
      ],
      series,
    } as EChartsOption;
  }, [timeline, categories, theme, t]);

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ width: '100%', height: `${chartHeight}px` }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
};
